import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ServitutRedegoerelsePhase } from "../model/report";
import { initialServitutReportState, type ServitutReportState } from "../model/report";
import type { ServitutReportAction } from "./actions";
import { servitutReportReducer } from "./reducer";

export type ServitutReportDispatch = (action: ServitutReportAction) => void;

type ServitutReportContextValue = {
   state: ServitutReportState;
   dispatch: ServitutReportDispatch;
   getState: () => ServitutReportState;
};

const ServitutReportContext = createContext<ServitutReportContextValue>({
   state: initialServitutReportState(),
   dispatch: () => undefined,
   getState: () => initialServitutReportState(),
});

export function ServitutReportProvider(props: {
   children: React.ReactNode;
   initialPhase?: ServitutRedegoerelsePhase;
   initialCaseDescription?: string;
}) {
   const [state, setState] = useState(() => initialServitutReportState({
      phase: props.initialPhase,
      caseDescription: props.initialCaseDescription,
   }));
   const stateRef = useRef(state);
   const dispatch = useCallback((action: ServitutReportAction) => {
      const next = servitutReportReducer(stateRef.current, action);
      stateRef.current = next;
      setState(next);
   }, []);
   const getState = useCallback(() => stateRef.current, []);
   const value = useMemo<ServitutReportContextValue>(() => ({
      state,
      dispatch,
      getState,
   }), [state, dispatch, getState]);
   return (
      <ServitutReportContext.Provider value={value}>
         {props.children}
      </ServitutReportContext.Provider>
   );
}

export function useServitutReport() {
   return useContext(ServitutReportContext);
}
